









































import Vue from 'vue';
import Component from 'vue-class-component';

// Store types
import { SpacesGetter, ToolSpace } from '@/store/store.types';

// Button types
import { ButtonOptions } from '@/components/input/types';

// Modal and input
import Modal from '@/components/modal/Modal.vue';
import Input from '@/components/input/Input.vue';

/**
 *  The event that is emitted on close.
 */
export interface CancelEvent {
    toolSpaceId: string;
    isNew: boolean;
}

/**
 *  The modal that is used to update the name of a space.
 */
@Component
export default class SpaceNameModal extends Vue {

    public $refs!: {
        modal: Modal;
        input: Input;
    };

    /**
     *  Holding a copy of the original name, so we can
     *  set it back when the user cancels the update.
     */
    private originalName: string | null = null;

    /**
     *  The (tool) space for which the name should be updated.
     */
    private space: ToolSpace | null = null;

    /**
     *  The error that is bound to the input.
     */
    private error: string | null = null;

    /**
     *
     *  If the space for which the name is updated is new.
     *
     *  (i) Given back to the parent on cancel so new
     *  spaces can be removed if canceled.
     *
     */
    private isNew = false;

    /**
     *  Holding the button settings of the available actions.
     */
    private actions: { [action: string]: ButtonOptions; } = {
        cancel: {
            title: 'Annuleer het aanpassen van de naam',
            content: 'Annuleren',
        },
        confirm: {
            title: 'Bevestig het aanpassen van de naam',
            content: 'Opslaan',
            disabled: false,
        },
    };

    /**
     *
     *  Used to open the modal in which the name of a space can be updated.
     *
     *  @param space The (tool) space to update the name for
     *  @param isNew If the space for which the name is updated is new
     *
     *  (i) The user is required to enter a valid name when it's about a new space
     *
     */
    public open(space: ToolSpace, isNew = false): void {

        this.space = space;

        this.originalName = space.data.name;

        this.isNew = isNew;

        /**
         *  Disable the confirm button by default
         *  if they are adding a new space
         */
        if (this.isNew) {

            this.actions.confirm.disabled = true;

        }

        this.$refs.modal.open();

        /**
         *  Give it a tick to render the modal and input, and
         *  programmatically focus the input to save a click.
         */
        this.$nextTick(() => {

            const input: HTMLInputElement | null = this.$refs.input.$el.querySelector('input');

            if (input) {

                input.focus();

            }

        });

    }

    /**
     *  Used to cancel the updating of the name.
     */
    private cancel(): void {

        if (!this.space) {

            return;

        }

        /**
         *  Restore the original name of the space.
         */
        this.space.data.name = this.originalName;

        /**
         *  Emit the cancel event, so the parent knows
         *  about this space it is and if it's new.
         */
        const event: CancelEvent = {
            toolSpaceId: this.space.data.tool_space_id as string,
            isNew: this.isNew,
        };

        this.$emit('cancel', event);

        /**
         *  Close the modal.
         */
        this.$refs.modal.close(true);

    }

    /**
     *
     *  Used to confirm the update of the name.
     *
     *  (i) The name is real-time updated because the
     *  input is bound to the original model, so we only
     *  need to close the modal on confirm.
     *
     */
    private confirm(): void {

        /**
         *  Just to be sure, because the button doesn't
         *  (/shouldn't) emit a click when disabled.
         */
        if (this.error) {

            return;

        }

        this.$refs.modal.close(true);

    }

    /**
     *
     *  Used to validate the current input for the name.
     *
     *  (i) Ensuring single, unique names without
     *  special characters are inserted.
     *
     */
    private validate(): void {

        if (!this.space) {

            return;

        }

        let error: string | null = null;

        /**
         *  First ensure a valid name is inserted, secondly ensure
         *  there isn't already a space with this name.
         */
        if (!this.space.data.name || !/^[a-z\u00C0-\u00FF ]{4,20}$/i.test(this.space.data.name)) {

            error = 'De ruimte moet één naam hebben die bestaat uit letters';

        } else {

            error = this._nameExists() ? 'Er bestaat al een ruimte met deze naam' : null;

        }

        this.error = error;

        this.actions.confirm.disabled = error !== null;

    }

    /**
     *  Used to check if an already existing space
     *  holds the currently inserted name.
     */
    private _nameExists(): boolean {

        if (!this.space) {

            return false;

        }

        for (const space of Object.values(this.$store.getters[SpacesGetter.ADDED]) as Array<ToolSpace>) {

            if (space === this.space) {

                continue;

            }

            if (space.data.name === this.space.data.name) {

                return true;

            }

        }

        return false;

    }

}
